import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { push } from 'connected-react-router';

import { clear_two_factor_key, clear_force_forgot_password, login, oauth2Login, two_factor_auth, two_factor_auth_resend, verifyUser, resolveLogin, userProfile } from '../../actions/auth';
import { validateCredential, CredentialType } from '../../utils/validation';
import { Background } from '../../components/Login/Background/Background';
import Strings from '../../Strings';
import { QueryString } from '../../utils';
import { getVariablesForBrand, setBrandFromLS, setBrand } from '../../utils/brand';
import { setTheme, setThemeFromLS } from '../../utils/colorTheme';
import { LoginForm } from './LoginForm';
import { TwoFAForm, TwoFactorMethod } from './TwoFAForm';
import { getDefaultPageForUser, getPagesForUser } from '../../utils/defaultPages';
import { BACKEND_PORT_LS_ID } from '../../constants';
import { notificationActions } from '../../components/Notification/redux/actions';

// Types
interface RootState {
  auth: {
    authenticated: boolean;
    profileRequested?: boolean;
    loginErrors?: { message: string };
    twoFAErrors?: { message: string };
    permissions?: unknown;
    is_doctor?: boolean;
    role?: string;
    two_factor_key?: string;
    two_factor_method?: string;
    verification_token?: string;
    verification_method?: string;
    code_expires_at?: number;
    phone_hint?: string;
    email_hint?: string;
    verified?: boolean;
    isLoading?: boolean;
    reset_password?: boolean;
    force_forgot_password?: boolean;
    force_forgot_password_method?: string;
    force_forgot_password_reason?: string;
    ssoResolverLoading?: boolean;
  };
}

interface LocationProps {
  search: string;
}

interface QueryParams {
  org?: string;
  backendPort?: string;
  ssoError?: string;
  ssoRef?: string;
  authentication_token?: string;
  origin?: string;
  sst?: string;
  next?: string;
}

const SSO_LOGIN_ERROR_MESSAGES: Record<string, string> = {
  invalid_credentials: Strings.invalidUsernameOrPassword,
  internal_error: Strings.internalError,
};

const getSsoErrorMessage = (errorCodeOrMessage: string, ref?: string): string => {
  const baseMessage = SSO_LOGIN_ERROR_MESSAGES[errorCodeOrMessage] || errorCodeOrMessage;
  if (!ref) {
    return baseMessage;
  }
  return `${baseMessage} Reference: ${ref}`;
};

// Map state to props
const mapStateToProps = (state: RootState) => ({
  authenticated: state.auth.authenticated,
  profileRequested: state.auth.profileRequested || false,
  loginErrors: state.auth.loginErrors,
  twoFAErrors: state.auth.twoFAErrors,
  permissions: state.auth.permissions,
  is_doctor: state.auth.is_doctor,
  role: state.auth.role || '',
  two_factor_key: state.auth.two_factor_key,
  two_factor_method: state.auth.two_factor_method,
  verification_token: state.auth.verification_token,
  verification_method: state.auth.verification_method,
  phone_hint: state.auth.phone_hint,
  email_hint: state.auth.email_hint,
  verified: state.auth.verified,
  isLoading: state.auth.isLoading || false,
  reset_password: state.auth.reset_password,
  force_forgot_password: state.auth.force_forgot_password,
  force_forgot_password_method: state.auth.force_forgot_password_method,
  force_forgot_password_reason: state.auth.force_forgot_password_reason,
  ssoResolverLoading: state.auth.ssoResolverLoading || false,
});

// Map dispatch to props
const mapDispatchToProps = (dispatch: any) => ({
  onSSOResolve: (identifier: string) => dispatch(resolveLogin(identifier)),
  onFHIRLaunch: (sst: string) => dispatch(oauth2Login({ sst })),
  onForgotClick: () => dispatch(push('/resetpassword')),
  onForgotClickWithLogin: (emailHint?: string, phoneHint?: string, method?: string, reason?: string) => {
    dispatch(clear_force_forgot_password());
    // Code already sent by backend, go directly to new password page
    dispatch(
      push({
        pathname: '/setnewpassword',
        search: '',
        hash: '',
        state: { loginAfterReset: true, emailHint, phoneHint, method, reason },
      }),
    );
  },
  clear2FA: () => dispatch(clear_two_factor_key()),
  clearForceForgotPassword: () => dispatch(clear_force_forgot_password()),
  onNavigate: (path: string) => dispatch(push(path)),
  loadProfile: () => dispatch(userProfile()),
  TwoFA: (code: string, two_factor_key: string, isRecoveryCode: boolean) =>
    dispatch(
      two_factor_auth({
        code,
        two_factor_key,
        is_recovery_code: isRecoveryCode,
      }),
    ),
  verifyUser: (request: { token: string; code: string }) => dispatch(verifyUser(request)),
  TwoFAResend: (two_factor_key: string) => dispatch(two_factor_auth_resend({ two_factor_key })),
  onAuthenticated: (queryParams: QueryParams) => {
    if (queryParams.next) {
      if (queryParams.next.startsWith('/oauth/authorize/')) {
        window.location.href = queryParams.next;
      } else {
        dispatch(push(queryParams.next));
      }
    } else {
      const url = localStorage.getItem('redirectUrl');
      if (url) {
        localStorage.removeItem('redirectUrl');
        const redirectUrl = decodeURIComponent(url);
        const redirectPath = new URL(redirectUrl).pathname;
        const validPages = getPagesForUser();
        const validPrefixes = new Set(
          validPages.map((p) => '/' + p.split('/').filter(Boolean)[0]),
        );
        if ([...validPrefixes].some((prefix) => redirectPath.startsWith(prefix))) {
          window.location.href = redirectUrl;
        } else {
          dispatch(push(getDefaultPageForUser()));
        }
      } else {
        dispatch(push(getDefaultPageForUser()));
      }
    }
  },
  onConfirm: (credential: string, password: string, credentialType: CredentialType) => {
    const payload: { password: string; username?: string; email?: string; mobile?: string } = { password };
    
    if (credentialType === CredentialType.Email) {
      payload.email = credential;
    } else if (credentialType === CredentialType.Phone) {
      payload.mobile = credential;
    } else {
      payload.username = credential;
    }
    
    return dispatch(login(payload as any));
  },
  showNotification: (message: string) => dispatch(notificationActions.show(message)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface LoginProps extends PropsFromRedux {
  location: LocationProps;
}

const LoginComponent: React.FC<LoginProps> = ({
  authenticated,
  profileRequested,
  role,
  permissions,
  loginErrors,
  twoFAErrors,
  two_factor_key,
  two_factor_method,
  verification_token,
  verification_method,
  phone_hint,
  email_hint,
  verified,
  isLoading,
  location,
  ssoResolverLoading,
  force_forgot_password,
  force_forgot_password_method,
  force_forgot_password_reason,
  onSSOResolve,
  onFHIRLaunch,
  onForgotClick,
  onForgotClickWithLogin,
  clear2FA,
  clearForceForgotPassword,
  onNavigate,
  loadProfile,
  TwoFA,
  TwoFAResend,
  onAuthenticated,
  onConfirm,
  verifyUser,
  showNotification,
}) => {
  const hasTriggeredRedirectRef = useRef(false);
  const isProfileLoadInFlightRef = useRef(false);
  const [formState, setFormState] = useState({
    username: '',
    password: '',
  });
  const [showSSOOnlyView, setShowSSOOnlyView] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [twoFACodeExpiresAt, setTwoFACodeExpiresAt] = useState<string | null>(null);
  const [codeIsResending, setCodeIsResending] = useState(false);

  const ensureHydratedAndRedirect = useCallback((params: QueryParams) => {
    if (!authenticated || hasTriggeredRedirectRef.current) {
      return;
    }

    const hasRole = Boolean(role);
    const hasPermissions = permissions !== undefined;

    if (hasRole && hasPermissions) {
      hasTriggeredRedirectRef.current = true;
      onAuthenticated(params);
      return;
    }

    if (!isProfileLoadInFlightRef.current) {
      isProfileLoadInFlightRef.current = true;
      loadProfile()
        .catch(() => null)
        .finally(() => {
          isProfileLoadInFlightRef.current = false;
        });
    }
  }, [authenticated, role, permissions, loadProfile, onAuthenticated]);

  // Initial setup on mount
  useEffect(() => {
    const query = new QueryString(location.search);
    const params = query.params as QueryParams;

    if (params.org) setBrand(params.org);
    if (params.org === 'caredx') setTheme('red');
    if (process.env.BACKEND === 'local' && params.backendPort) {
      localStorage.setItem(BACKEND_PORT_LS_ID, params.backendPort);
    }

    if (params.ssoError) {
      setErrors([getSsoErrorMessage(params.ssoError, params.ssoRef)]);
    }

    if (params.authentication_token) {
      sessionStorage.setItem('authentication_token', params.authentication_token);
      // Bootstrap auth from session storage on a clean URL first.
      // Calling onAuthenticated here can navigate before Redux auth state is ready.
      const nextParams = new URLSearchParams(location.search);
      nextParams.delete('authentication_token');
      const nextSearch = nextParams.toString();
      const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
      window.location.replace(nextUrl);
      return;
    }

    if (params.origin === 'fhir' && params.sst) {
      onFHIRLaunch(params.sst);
    }

    setBrandFromLS();
    setThemeFromLS();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle login errors
  useEffect(() => {
    if (loginErrors?.message) {
      setErrors([loginErrors.message]);
    }
  }, [loginErrors]);

  // Handle 2FA errors
  useEffect(() => {
    if (twoFAErrors?.message) {
      setErrors([twoFAErrors.message]);
    } else if (!twoFAErrors) {
      // Clear errors when twoFAErrors is cleared
      if (two_factor_key) {
        setErrors([]);
      }
    }
  }, [twoFAErrors, two_factor_key]);

  // Handle force forgot password for inactive users
  useEffect(() => {
    if (force_forgot_password) {
      onForgotClickWithLogin(email_hint, phone_hint, force_forgot_password_method, force_forgot_password_reason);
    }
  }, [force_forgot_password, email_hint, phone_hint, force_forgot_password_method, force_forgot_password_reason, onForgotClickWithLogin]);

  // Handle authentication/profile hydration state changes
  useEffect(() => {
    if (!authenticated) {
      return;
    }
    const query = new QueryString(location.search);
    const params = query.params as QueryParams;
    ensureHydratedAndRedirect(params);
  }, [authenticated, location.search, ensureHydratedAndRedirect]);

  const handleLogin = useCallback((event: React.FormEvent) => {
    event.preventDefault();
    setErrors([]);
    const { username, password } = formState;
    const credentialResult = validateCredential(username);
    const credentialValue = credentialResult.normalizedValue || username;
    
    onConfirm(credentialValue, password, credentialResult.type)
      .then((r: any) => {
        if (r?.response?.code_expires_at) {
          setTwoFACodeExpiresAt(r.response.code_expires_at);
        }
      })
      .catch((err: Error) => console.error('Login error:', err));
  }, [formState, onConfirm]);

  const handleSSOLogin = useCallback(() => {
    const identifier = formState.username.trim();
    if (!identifier) {
      setErrors([]);
      setShowSSOOnlyView(true);
      return;
    }
    setErrors([]);
    onSSOResolve(identifier)
      .then((r: any) => {
        if (r?.response?.sso_available && r?.response?.redirect_url) {
          window.location.href = r.response.redirect_url;
        } else {
          setErrors([r?.response?.message || 'Unable to continue with SSO.']);
        }
      })
      .catch(() => {
        setErrors(['Unable to continue with SSO.']);
      });
  }, [formState.username, onSSOResolve]);

  const handleBackToLogin = useCallback(() => {
    setErrors([]);
    setShowSSOOnlyView(false);
  }, []);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormState(prev => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handle2FAFormSubmit = useCallback(({ code, isRecoveryCode }: { code: string; isRecoveryCode: boolean }) => {
    if (two_factor_key) {
      TwoFA(code, two_factor_key, isRecoveryCode);
    }
  }, [two_factor_key, TwoFA]);

  const handle2FABack = useCallback((event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
    }
    clear2FA();
  }, [clear2FA]);

  const handle2FAResend = useCallback(() => {
    if (!two_factor_key) return;

    setCodeIsResending(true);
    TwoFAResend(two_factor_key).then((r: any) => {
      setCodeIsResending(false);

      const error = r?.response?.error;

      if (error) {
        setErrors([error]);
      } else if (r?.response?.code_expires_at) {
        setTwoFACodeExpiresAt(r.response.code_expires_at);
      }
    });
  }, [two_factor_key, TwoFAResend]);

  // Parse 2FA method from string to enum
  const get2FAMethod = (): TwoFactorMethod => {
    switch (two_factor_method) {
      case 'sms':
        return TwoFactorMethod.Sms;
      case 'email':
        return TwoFactorMethod.Email;
      case 'totp':
      default:
        return TwoFactorMethod.Totp;
    }
  };

  const handleVerificationFormSubmit = useCallback(({ code }: { code: string }) => {
    if (!verification_token) return;
    verifyUser({code, token: verification_token}).then((r: any) => {
      if (r?.response?.verified) {
        showNotification('Your account has been successfully verified. You can now log in.');
      }
    });
  }, [verifyUser, verification_token]);

  const query = new QueryString(location.search);
  const params = query.params as QueryParams;
  const isEnabled = formState.username.length > 0 && formState.password.length > 0;

  if (params.origin === 'fhir') {
    return <Background title={Strings.loadingFhirSessions} noLines />;
  }

  let form;

  if (verification_token) {
    form = (
      <TwoFAForm
        onFormSubmit={handleVerificationFormSubmit}
        onBack={handle2FABack}
        error={errors[0]}
        isLoading={isLoading || codeIsResending}
        expiresAt={twoFACodeExpiresAt || undefined}
        twoFactorMethod={verification_method === 'sms' ? TwoFactorMethod.Sms : TwoFactorMethod.Email}
        verifyAccount
        phoneHint={phone_hint}
        emailHint={email_hint}
      />
    );
  } else if (two_factor_key) {
    if (!verified) {
      onNavigate('/invite-doctor');
    } else {
      form = (
        <TwoFAForm
          onFormSubmit={handle2FAFormSubmit}
          onBack={handle2FABack}
          onResend={handle2FAResend}
          error={errors[0]}
          isLoading={isLoading || codeIsResending}
          expiresAt={twoFACodeExpiresAt || undefined}
          twoFactorMethod={get2FAMethod()}
          phoneHint={phone_hint}
          emailHint={email_hint}
        />
      );
    }
  } else {
    form = (
      <LoginForm
        state={formState}
        errors={errors}
        onLogin={handleLogin}
        onChange={handleChange}
        onForgotClick={onForgotClick}
        isEnabled={isEnabled}
        isLoading={isLoading}
        onSSOLogin={handleSSOLogin}
        onBackToLogin={handleBackToLogin}
        ssoResolverLoading={ssoResolverLoading}
        ssoOnly={showSSOOnlyView}
      />
    );
  }

  const brandVariables = getVariablesForBrand();
  return (
    <Background
      title={
        verification_token
          ? 'Verify Your Account'
          : showSSOOnlyView
            ? `${Strings.continueWithSSO}`
            : `${Strings.loginTo} ${brandVariables.name}`
      }
      noLines
    >
      {form}
    </Background>
  );
};

export default connector(LoginComponent);
